import React, {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import {
  closeSession,
  fetchCaixaDashboard,
  fetchOpenSession,
} from "../../modules/caixa-financeiro/utils/caixa.api";
import { fetchCardapioPedidos, CARDAPIO_PEDIDO_STATUS } from "../../modules/cardapio/utils/cardapio.api";

export interface ValoresCaixa {
  id: number;
  user_id: number;
  aberto: boolean;
  aberto_em: Date;
  valor_troco: number;
  valor_recebido: number;
  valor_em_aberto: number;
  total_caixa: number;
}

interface CaixaContextProps {
  caixaAberto: boolean;
  valores: ValoresCaixa;
  abrirCaixa: (trocoInicial: number) => Promise<void>;
  fecharCaixa: (temUsuariosNoClube: boolean) => Promise<boolean>;
  atualizarValores: (novosValores: Partial<ValoresCaixa>) => void;
  adicionarPagamento: (tipo: "Dinheiro" | "Outros", valor: number) => void;
  atualizarEmAberto: (valor: number) => void;
  gerarRelatorio: (tipo: "simplificado" | "completo") => any;
  verificaCaixaAberto(): void;
}

export const CaixaContext = createContext<CaixaContextProps>(
  {} as CaixaContextProps,
);

const EMPTY_VALORES: ValoresCaixa = {
  id: 0,
  aberto: false,
  aberto_em: new Date(),
  total_caixa: 0,
  user_id: 0,
  valor_em_aberto: 0,
  valor_recebido: 0,
  valor_troco: 0,
};

interface ProviderProps {
  children: ReactNode;
}

export const CaixaProvider: React.FC<ProviderProps> = ({ children }) => {
  const [caixaAberto, setCaixaAberto] = useState<boolean>(false);
  const [valores, setValores] = useState<ValoresCaixa>(EMPTY_VALORES);

  const verificaCaixaAberto = useCallback(() => {
    fetchOpenSession()
      .then(async (session) => {
        if (!session?._id || session.status !== "aberto") {
          setCaixaAberto(false);
          setValores(EMPTY_VALORES);
          return;
        }

        const [dash, pedidos] = await Promise.all([
          fetchCaixaDashboard().catch(() => null),
          fetchCardapioPedidos(
            undefined,
            CARDAPIO_PEDIDO_STATUS.EM_ABERTO,
          ).catch(() => []),
        ]);

        const totalSales = Number(dash?.total_sales ?? 0);
        const opening = Number(session.opening_fund ?? 0);
        
        // Correção aplicada aqui: type casting de 'pedidos' para 'any[]'
        const emAberto = ((pedidos as any[]) || []).reduce(
          (sum: number, p: any) => sum + Number(p.total || 0),
          0,
        );

        setValores({
          id: session._id,
          aberto: true,
          aberto_em: new Date(session.opened_at),
          user_id: session._id_operator,
          valor_troco: opening,
          valor_recebido: totalSales,
          valor_em_aberto: emAberto,
          total_caixa: opening + totalSales,
        });
        setCaixaAberto(true);
      })
      .catch((err) => {
        console.error("Erro ao verificar caixa financeiro:", err);
        setCaixaAberto(false);
        setValores(EMPTY_VALORES);
      });
  }, []);

  useEffect(() => {
    verificaCaixaAberto();
    const onChanged = () => verificaCaixaAberto();
    window.addEventListener("caixa:session-changed", onChanged);
    window.addEventListener("cardapio:pedido-changed", onChanged);
    return () => {
      window.removeEventListener("caixa:session-changed", onChanged);
      window.removeEventListener("cardapio:pedido-changed", onChanged);
    };
  }, [verificaCaixaAberto]);

  const abrirCaixa = useCallback(async (_trocoInicial: number) => {
    toast.info("Abra o caixa em Caixa Financeiro (/caixa-financeiro).");
    window.location.href = "/caixa-financeiro/abertura";
  }, []);

  const fecharCaixa = useCallback(
    async (temUsuariosNoClube: boolean): Promise<boolean> => {
      if (!valores.id) {
        toast.error("Nenhum caixa aberto");
        return false;
      }

      if (temUsuariosNoClube) {
        toast.error("Não é possível fechar o caixa com usuários no clube.");
        return false;
      }

      if (valores.valor_em_aberto > 0) {
        const confirma = window.confirm(
          "Ainda há valores em aberto. Deseja realmente fechar o caixa?",
        );
        if (!confirma) return false;
      }

      try {
        await closeSession(valores.id, { closing_amount: valores.total_caixa });
        setValores(EMPTY_VALORES);
        setCaixaAberto(false);
        toast.success("Caixa fechado com sucesso!");
        return true;
      } catch {
        toast.error("Erro ao fechar caixa!");
        return false;
      }
    },
    [valores],
  );

  const atualizarValores = useCallback(
    (novosValores: Partial<ValoresCaixa>) => {
      setValores((prev) => ({
        ...prev,
        ...novosValores,
      }));
    },
    [],
  );

  const adicionarPagamento = useCallback(
    (tipo: "Dinheiro" | "Outros", valor: number) => {
      const v = Number(valor);
      setValores((prev) => ({
        ...prev,
        valor_recebido: Number(prev.valor_recebido) + v,
        valor_em_aberto: Math.max(0, Number(prev.valor_em_aberto) - v),
        total_caixa: Number(prev.total_caixa) + v,
      }));
      void tipo;
    },
    [],
  );

  const atualizarEmAberto = useCallback((valor: number) => {
    setValores((prev) => ({
      ...prev,
      valor_em_aberto: valor,
    }));
  }, []);

  const gerarRelatorio = useCallback((_tipo: "simplificado" | "completo") => {},
  []);

  return (
    <CaixaContext.Provider
      value={{
        caixaAberto,
        valores,
        abrirCaixa,
        fecharCaixa,
        atualizarValores,
        adicionarPagamento,
        atualizarEmAberto,
        gerarRelatorio,
        verificaCaixaAberto,
      }}
    >
      {children}
    </CaixaContext.Provider>
  );
};